import { dismiss, remove, upsert } from "./store";
import { type Toast, type Renderable, type DefaultToastOptions, type ToastOptions, type ToastType, type ValueOrFunction, resolveValue } from "./types";
import { genId } from "./utils";
import { clientData } from "$lib/client-data/index.svelte.js";
type Message<T extends Record<string, unknown> = Record<string, unknown>> = Renderable<T>;

type ToastHandler = <T extends Record<string, unknown> = Record<string, unknown>>(message: Message<T>, options?: ToastOptions<T>) => string;

const createToast = <T extends Record<string, unknown> = Record<string, unknown>>(message: Message<T>, type: ToastType = "blank", opts?: ToastOptions<T>): Toast<T> => ({
  createdAt: Date.now(),
  visible: true,
  type,
  ariaProps: {
    role: "status",
    "aria-live": "polite",
  },
  message,
  pauseDuration: 0,
  icon: opts?.icon,
  duration: opts?.duration,
  iconTheme: opts?.iconTheme,
  position: opts?.position,
  props: opts?.props,
  id: opts?.id || genId(),
});

const createHandler =
  (type?: ToastType): ToastHandler =>
  (message, options) => {
    if (!clientData.toasterShow) {
      clientData.toasterShow = true;
    }
    const toast = createToast(message, type, options);
    upsert(toast);
    return toast.id;
  };

const toast = <T extends Record<string, unknown> = Record<string, unknown>>(message: Message<T>, opts?: ToastOptions<T>) => createHandler("blank")(message, opts);

toast.error = createHandler("error");
toast.success = createHandler("success");
toast.loading = createHandler("loading");
toast.custom = createHandler("custom");
toast.cancel = createHandler("cancel");
toast.warning = createHandler("warning");
toast.dismiss = (toastId?: string) => {
  dismiss(toastId);
};

toast.remove = (toastId?: string) => remove(toastId);

toast.promise = <T>(
  promise: Promise<T>,
  msgs: {
    loading: Renderable;
    success: Message<Record<string, unknown>>;
    error: Message<Record<string, unknown>>;
    cancel?: Message<Record<string, unknown>>;
  },
  opts?: DefaultToastOptions
) => {
  const id = toast.loading(msgs.loading, { ...opts, ...opts?.loading });

  promise
    .then((p) => {
      if (p) {
        if (msgs.success) {
          toast.success(msgs.success, {
            id,
            ...opts,
            ...opts?.success,
          });
        } else if (msgs.cancel) {
          toast.cancel(msgs.cancel, {
            id,
            ...opts,
            ...opts?.cancel,
          });
        }
      } else {
        toast.error(msgs.error, {
          id,
          ...opts,
          ...opts?.error,
        });
      }

      return p;
    })
    .catch((e) => {
      toast.error(msgs.error, {
        id,
        ...opts,
        ...opts?.error,
      });
    });

  return promise;
};

export default toast;
